import type { Goal, Profile, ProfileUpdate } from "@bioflow/contracts";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMe, useUpdateProfile } from "../../api/hooks";
import { BodyFields, EMPTY_BODY, GoalPicker, parseBody, type BodyValues } from "../../components/ProfileFields";
import { ONBOARDING_STEPS, done, effect, goal as goalText, how, params, start, why, type OnboardingStep } from "../../content/onboarding";
import { hapticNotify, hasTelegramContext } from "../../lib/telegram";
import { DoneStep, EffectStep, HowStep, StartStep, WhyStep } from "./OnboardingSteps";

/**
 * Онбординг — семь экранов ТЗ под одним адресом /onboarding/<шаг>.
 *
 * Выбор цели и параметры живут в состоянии этого компонента: роутер не
 * пересоздаёт элемент при смене параметра, поэтому «назад» с экрана 6 на 5
 * возвращает выбранную цель. На сервер данные уходят по частям — цель после
 * экрана 5, параметры после 6 — и любой сохранённый шаг отмечает онбординг
 * пройденным. Вне Telegram сохранять некуда: шаги просто листаются.
 */
export function OnboardingScreen() {
  const { step = "start" } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const update = useUpdateProfile();

  const [goal, setGoal] = useState<Goal | null>(null);
  const [body, setBody] = useState<BodyValues>(EMPTY_BODY);
  const [error, setError] = useState<string | null>(null);

  // Повторный проход (из профиля «Изменить данные» сюда не ведёт, но по
  // прямой ссылке — может): подставляем то, что уже сохранено.
  useEffect(() => {
    const profile = me.data?.profile;
    if (!profile) return;
    setGoal((current) => current ?? profile.goal);
    setBody((current) =>
      current === EMPTY_BODY
        ? {
            sex: profile.sex,
            heightCm: profile.heightCm?.toString() ?? "",
            weightKg: profile.weightKg?.toString() ?? "",
          }
        : current,
    );
  }, [me.data?.profile]);

  const index = ONBOARDING_STEPS.indexOf(step as OnboardingStep);
  if (index < 0) return <Navigate to="/onboarding/start" replace />;

  const go = (next: OnboardingStep) => navigate(`/onboarding/${next}`);

  const save = async (patch: ProfileUpdate, next: () => void) => {
    setError(null);
    if (!hasTelegramContext()) {
      next();
      return;
    }
    try {
      await update.mutateAsync(patch);
      next();
    } catch {
      hapticNotify("error");
      setError("Не удалось сохранить. Проверьте соединение и попробуйте ещё раз.");
    }
  };

  const parsed = parseBody(body);
  const paramsValid = parsed.heightCm !== null && parsed.weightKg !== null;

  /** Локальное превью для экрана «Готово», если сервер ещё не ответил или мы вне Telegram. */
  const preview: Profile = me.data?.profile ?? {
    sex: body.sex,
    heightCm: parsed.heightCm,
    weightKg: parsed.weightKg,
    bmi: paramsValid ? Math.round((parsed.weightKg! / (parsed.heightCm! / 100) ** 2) * 10) / 10 : null,
    goal,
  };

  const steps: Record<OnboardingStep, { body: React.ReactNode; label: string; onNext: () => void; disabled?: boolean; secondary?: React.ReactNode }> = {
    start: {
      body: <StartStep />,
      label: start.button,
      onNext: () => go("how"),
      secondary: (
        <p className="onb__skip">
          {start.skipPrefix} →{" "}
          <button type="button" className="link" onClick={() => void save({}, () => navigate("/", { replace: true }))}>
            {start.skipLink}
          </button>
        </p>
      ),
    },
    how: { body: <HowStep />, label: how.button, onNext: () => go("effect") },
    effect: { body: <EffectStep />, label: effect.button, onNext: () => go("why") },
    why: { body: <WhyStep />, label: why.button, onNext: () => go("goal") },
    goal: {
      body: (
        <>
          <h1 className="onb__title">{goalText.title}</h1>
          <p className="onb__subtitle">{goalText.subtitle}</p>
          <div style={{ marginTop: 16 }}>
            <GoalPicker value={goal} onChange={setGoal} />
          </div>
        </>
      ),
      label: goalText.button,
      disabled: goal === null,
      onNext: () => void save(goal ? { goal } : {}, () => go("params")),
    },
    params: {
      body: (
        <>
          <h1 className="onb__title">{params.title}</h1>
          <p className="onb__subtitle">{params.subtitle}</p>
          <div style={{ marginTop: 16 }}>
            <BodyFields values={body} onChange={(patch) => setBody((current) => ({ ...current, ...patch }))} />
          </div>
        </>
      ),
      label: params.button,
      disabled: !paramsValid,
      onNext: () =>
        void save(
          { ...(body.sex ? { sex: body.sex } : {}), heightCm: parsed.heightCm!, weightKg: parsed.weightKg! },
          () => go("done"),
        ),
      secondary: (
        <button type="button" className="link onb__skip" onClick={() => void save({}, () => go("done"))}>
          {params.skip}
        </button>
      ),
    },
    done: {
      body: <DoneStep profile={preview} />,
      label: done.button,
      onNext: () => navigate("/", { replace: true }),
    },
  };

  const current = steps[step as OnboardingStep];

  return (
    <div className="onb">
      <ol className="dots" aria-label={`Шаг ${index + 1} из ${ONBOARDING_STEPS.length}`}>
        {ONBOARDING_STEPS.map((name, i) => (
          <li key={name} className={`dots__item${i <= index ? " dots__item--on" : ""}`} />
        ))}
      </ol>

      <div className="onb__body">{current.body}</div>

      <div className="onb__footer">
        {error && <div className="alert" role="alert">{error}</div>}
        <button type="button" className="button" onClick={current.onNext} disabled={current.disabled || update.isPending}>
          {update.isPending ? "Сохраняем…" : current.label}
        </button>
        {current.secondary}
      </div>
    </div>
  );
}
